import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Lightning,
  CheckCircle,
  Clock,
  EnvelopeSimple,
  MagnifyingGlass,
  DownloadSimple,
  CaretUp,
  CaretDown,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const SortHeader = ({ label, field, sortConfig, onSort, align }) => {
  const active = sortConfig.field === field;
  const dir = active ? sortConfig.dir : null;
  return (
    <th
      className={`px-4 py-2.5 text-slate-600 font-semibold text-xs whitespace-nowrap cursor-pointer select-none hover:bg-slate-100 transition-colors ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col leading-none">
          <CaretUp size={10} weight={active && dir === 'asc' ? 'bold' : 'regular'} className={active && dir === 'asc' ? 'text-[#0066FF]' : 'text-slate-300'} />
          <CaretDown size={10} weight={active && dir === 'desc' ? 'bold' : 'regular'} className={active && dir === 'desc' ? 'text-[#0066FF]' : 'text-slate-300'} />
        </span>
      </span>
    </th>
  );
};

const ProvincesActivePage = () => {
  const { axiosInstance } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUADM, setFilterUADM] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ field: 'province', dir: 'asc' });

  const fetchProvinces = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/provinces/active');
      setProvinces(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { fetchProvinces(); }, [fetchProvinces]);

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleExport = () => window.open(`${API_URL}/api/provinces/export`, '_blank');

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };

  const formatDateTime = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const statusLabel = (s) => {
    if (s === 'sent') return 'Inviata';
    if (s === 'test_sent') return 'Test inviato';
    return 'Da inviare';
  };

  // Unique UADM values for filter
  const uadmOptions = useMemo(() => {
    return [...new Set(provinces.map((p) => p.customs_office))].filter(Boolean).sort();
  }, [provinces]);

  // Filter + Search + Sort
  const processedData = useMemo(() => {
    let data = [...provinces];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter((p) =>
        p.province?.toLowerCase().includes(q) ||
        p.customs_office?.toLowerCase().includes(q) ||
        p.customs_pec?.toLowerCase().includes(q)
      );
    }
    if (filterStatus) {
      data = data.filter((p) => {
        if (filterStatus === 'sent') return p.communication_status === 'sent';
        if (filterStatus === 'test_sent') return p.communication_status === 'test_sent';
        return p.communication_status !== 'sent' && p.communication_status !== 'test_sent';
      });
    }
    if (filterUADM) {
      data = data.filter((p) => p.customs_office === filterUADM);
    }
    // Sort
    data.sort((a, b) => {
      const f = sortConfig.field;
      let va = a[f], vb = b[f];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortConfig.dir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '');
      vb = String(vb || '');
      return sortConfig.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return data;
  }, [provinces, searchTerm, filterStatus, filterUADM, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(processedData.length / PAGE_SIZE));
  const paginatedData = processedData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterStatus, filterUADM]);

  // Totals for the summary bar
  const totals = useMemo(() => ({
    provinces: processedData.length,
    pods: processedData.reduce((s, p) => s + (p.pod_count || 0), 0),
    consumption: processedData.reduce((s, p) => s + (p.total_consumption_kwh || 0), 0),
    excise: processedData.reduce((s, p) => s + (p.total_annual_excise || 0), 0),
    sent: processedData.filter((p) => p.communication_status === 'sent').length,
  }), [processedData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0A2540] border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-5" data-testid="provinces-active-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">Province Attive</h1>
          <p className="mt-1 text-sm text-slate-600">
            {totals.provinces} province · {totals.pods} POD totali · {totals.sent} comunicate
          </p>
        </div>
        <Button onClick={handleExport} variant="outline" className="rounded-sm shrink-0" data-testid="export-provinces-btn">
          <DownloadSimple size={16} className="mr-2" /> Esporta Excel
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3">
          <p className="text-xs text-slate-500">Province Filtrate</p>
          <p className="text-xl font-bold text-slate-900" data-testid="summary-provinces">{totals.provinces}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3">
          <p className="text-xs text-slate-500">POD Totali</p>
          <p className="text-xl font-bold text-slate-900" data-testid="summary-pods">{totals.pods}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3">
          <p className="text-xs text-slate-500">Consumo Annuo kWh</p>
          <p className="text-lg font-bold text-slate-900 font-mono" data-testid="summary-consumption">{totals.consumption.toLocaleString('it-IT')}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3">
          <p className="text-xs text-slate-500">Accise Annue</p>
          <p className="text-lg font-bold text-slate-900 font-mono" data-testid="summary-excise">EUR {totals.excise.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm px-4 py-3">
          <p className="text-xs text-slate-500">Comunicate</p>
          <p className="text-xl font-bold text-green-700" data-testid="summary-sent">{totals.sent} / {totals.provinces}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Cerca provincia, UADM, PEC..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0066FF] focus:border-[#0066FF]"
            data-testid="search-provinces"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#0066FF]"
          data-testid="filter-status"
        >
          <option value="">Tutti gli stati</option>
          <option value="pending">Da inviare</option>
          <option value="test_sent">Test inviato</option>
          <option value="sent">Inviata</option>
        </select>
        <select
          value={filterUADM}
          onChange={(e) => setFilterUADM(e.target.value)}
          className="text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#0066FF] max-w-[220px]"
          data-testid="filter-uadm"
        >
          <option value="">Tutti gli UADM</option>
          {uadmOptions.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
        {(searchTerm || filterStatus || filterUADM) && (
          <button
            onClick={() => { setSearchTerm(''); setFilterStatus(''); setFilterUADM(''); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline"
            data-testid="clear-filters"
          >
            Pulisci filtri
          </button>
        )}
      </div>

      {/* Table */}
      {processedData.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-8 text-center">
          <Lightning size={32} className="mx-auto text-slate-400 mb-3" />
          <p className="text-sm text-slate-600">
            {provinces.length === 0 ? 'Nessuna provincia attiva. Importa prima l\'anagrafica POD.' : 'Nessun risultato con i filtri applicati.'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full" data-testid="provinces-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <SortHeader label="Provincia" field="province" sortConfig={sortConfig} onSort={handleSort} />
                  <SortHeader label="POD Attivi" field="pod_count" sortConfig={sortConfig} onSort={handleSort} align="right" />
                  <SortHeader label="Consumo kWh" field="total_consumption_kwh" sortConfig={sortConfig} onSort={handleSort} align="right" />
                  <SortHeader label="Accise EUR" field="total_annual_excise" sortConfig={sortConfig} onSort={handleSort} align="right" />
                  <SortHeader label="UADM" field="customs_office" sortConfig={sortConfig} onSort={handleSort} />
                  <th className="px-4 py-2.5 text-slate-600 font-semibold text-xs whitespace-nowrap text-left">PEC UADM</th>
                  <SortHeader label="Inizio Fornitura" field="first_activation" sortConfig={sortConfig} onSort={handleSort} align="center" />
                  <SortHeader label="Comunicazione" field="communication_status" sortConfig={sortConfig} onSort={handleSort} align="center" />
                  <SortHeader label="Data Com." field="communication_date" sortConfig={sortConfig} onSort={handleSort} align="center" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedData.map((prov) => (
                  <tr key={prov.province} className="hover:bg-slate-50 transition-colors" data-testid={`province-row-${prov.province}`}>
                    <td className="px-4 py-3 text-sm font-bold text-slate-900">{prov.province}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right font-mono">{prov.pod_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right font-mono">
                      {(prov.total_consumption_kwh || 0).toLocaleString('it-IT')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right font-mono">
                      {(prov.total_annual_excise || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">{prov.customs_office}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 break-all max-w-[200px]">{prov.customs_pec}</td>
                    <td className="px-4 py-3 text-center text-xs text-slate-700">{formatDate(prov.first_activation)}</td>
                    <td className="px-4 py-3 text-center">
                      {prov.communication_status === 'sent' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-sm border border-green-200" data-testid={`comm-sent-${prov.province}`}>
                          <CheckCircle size={12} weight="fill" /> Inviata
                        </span>
                      ) : prov.communication_status === 'test_sent' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-sm border border-blue-200" data-testid={`comm-test-${prov.province}`}>
                          <EnvelopeSimple size={12} /> Test
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-sm border border-amber-200" data-testid={`comm-pending-${prov.province}`}>
                          <Clock size={12} /> Da inviare
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-slate-700">{formatDateTime(prov.communication_date)}</td>
                  </tr>
                ))}
              </tbody>
              {/* Totals Footer */}
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-bold text-sm">
                  <td className="px-4 py-3 text-slate-900">TOTALE</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{totals.pods}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{totals.consumption.toLocaleString('it-IT')}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{totals.excise.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                  <td colSpan={5}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>
                Pagina {currentPage} di {totalPages} · {processedData.length} risultati
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} className="rounded-sm" data-testid="prev-page">
                  <CaretLeft size={14} />
                </Button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) { pageNum = i + 1; }
                  else if (currentPage <= 4) { pageNum = i + 1; }
                  else if (currentPage >= totalPages - 3) { pageNum = totalPages - 6 + i; }
                  else { pageNum = currentPage - 3 + i; }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`rounded-sm w-8 h-8 p-0 ${currentPage === pageNum ? 'bg-[#0A2540] text-white' : ''}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button size="sm" variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} className="rounded-sm" data-testid="next-page">
                  <CaretRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProvincesActivePage;
